import React, { useEffect, useState, useCallback } from 'react';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  Upload as UploadIcon,
  InfoOutlined as InfoOutlinedIcon,
  ShieldOutlined as ShieldOutlinedIcon
} from '@mui/icons-material';

import PageTitle from '../../../components/PageTitle';
import { sendRequest } from '../../../utils/dateHelpers';
import { ROLE_NAMES, ROLES } from '../../../constants/rolePermissions';
import BrandedSelect from '../../../components/Form/BrandedSelect';
import Loading from '../../../components/Loading';
import { Skeleton } from '../../../components/ui';

/* ================= API ENDPOINTS ================= */
const API_GET_INVITE_USER_URL = '/invite/get-invite-user';
const API_SET_INVITE_USER_URL = '/invite/set-invite-user';
const API_GET_USERS_URL = '/settings/users/get';
const API_GET_PLAN_SETTINGS_URL = '/billing/usage';
const API_CANCEL_INVITE_URL = '/invite/cancel-invite';
const SETUP_SEAT_ALLOWANCE = {
  name: 'Plan selection pending',
  seats: 10,
  isSetupAllowance: true
};
const InviteUser = () => {
  const [invitations, setInvitations] = useState([
    { email: '', role: ROLES.VIEWER }
  ]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingSentInvitations, setFetchingSentInvitations] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [invitationErrors, setInvitationErrors] = useState([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkRole, setBulkRole] = useState(ROLES.VIEWER);
  const [bulkError, setBulkError] = useState('');
  const [isRoleInfoModalOpen, setIsRoleInfoModalOpen] = useState(false);
  const [existingUserCount, setExistingUserCount] = useState(0);
  const [planSeatInfo, setPlanSeatInfo] = useState(SETUP_SEAT_ALLOWANCE);
  const [isSeatLimitModalOpen, setIsSeatLimitModalOpen] = useState(false);
  const [seatLimitMessage, setSeatLimitMessage] = useState('');
  const [cancellingInvitationId, setCancellingInvitationId] = useState(null);

  const maxSeats = planSeatInfo?.seats || SETUP_SEAT_ALLOWANCE.seats;
  const pendingInvitationCount = sentInvitations.filter(invitation => {
    const status = invitation?.status?.toLowerCase?.() || 'pending';
    return status === 'pending';
  }).length;
  const usedSeats = existingUserCount + pendingInvitationCount;
  const availableSeats = Math.max(0, maxSeats - usedSeats);

  const openSeatLimitModal = message => {
    setSeatLimitMessage(message);
    setIsSeatLimitModalOpen(true);
  };
  const getSeatLimitMessage = actionText => {
    if (planSeatInfo.isSetupAllowance) {
      return `You can invite up to ${maxSeats} total seat(s) during setup before selecting a plan. ${usedSeats} seat(s) are already in use, so you can invite ${availableSeats} more user(s). Choose a plan with enough seats before adding more teammates.`;
    }

    return `Your current plan is ${planSeatInfo.name} with ${maxSeats} seats. ${usedSeats} seat(s) are already in use, so you can invite ${availableSeats} more user(s). ${actionText}`;
  };

  const getFilledInvitationCount = useCallback(
    list => list.filter(invitation => invitation?.email?.trim()).length,
    []
  );

  const handleAddInvitation = () => {
    const filledInvitationCount = getFilledInvitationCount(invitations);
    if (filledInvitationCount >= availableSeats) {
      openSeatLimitModal(
        getSeatLimitMessage('Upgrade your plan to invite more teammates.')
      );
      return;
    }
    setInvitations([...invitations, { email: '', role: ROLES.VIEWER }]);
  };

  const handleRemoveInvitation = index => {
    const newInvitations = [...invitations];
    newInvitations.splice(index, 1);
    setInvitations(newInvitations);
  };

  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleBulkAdd = () => {
    setBulkError('');

    if (!bulkEmails.trim()) {
      setBulkError('Please enter at least one email address.');
      return;
    }

    // Split by comma, newline, or semicolon, then filter and trim
    const emailList = bulkEmails
      .split(/[,\n;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emailList.length === 0) {
      setBulkError('No valid email addresses found.');
      return;
    }

    // Validate all emails
    const invalidEmails = emailList.filter(email => !validateEmail(email));
    if (invalidEmails.length > 0) {
      setBulkError(
        `Invalid email addresses: ${invalidEmails.slice(0, 3).join(', ')}${
          invalidEmails.length > 3 ? '...' : ''
        }`
      );
      return;
    }

    // Remove duplicates
    const uniqueEmails = [...new Set(emailList)];

    // Add to invitations list
    const newInvitations = uniqueEmails.map(email => ({
      email,
      role: bulkRole
    }));

    // Filter out emails that already exist in the current invitations
    const existingEmails = invitations
      .map(inv => inv.email.toLowerCase())
      .filter(email => email);
    const filteredNewInvitations = newInvitations.filter(
      inv => !existingEmails.includes(inv.email.toLowerCase())
    );

    if (filteredNewInvitations.length === 0) {
      setBulkError('All email addresses are already in the list.');
      return;
    }

    const filledInvitationCount = getFilledInvitationCount(invitations);
    const remainingInviteCapacity = Math.max(
      0,
      availableSeats - filledInvitationCount
    );

    if (filteredNewInvitations.length > remainingInviteCapacity) {
      openSeatLimitModal(
        getSeatLimitMessage('Upgrade your plan to add more teammates.')
      );
      return;
    }

    if (filteredNewInvitations.length < newInvitations.length) {
      const skippedCount =
        newInvitations.length - filteredNewInvitations.length;
      setBulkError(
        `${skippedCount} duplicate email(s) skipped. Added ${filteredNewInvitations.length} email(s).`
      );
    }

    // Add new invitations to the list
    setInvitations([...invitations, ...filteredNewInvitations]);

    // Reset modal
    setBulkEmails('');
    setBulkRole(ROLES.VIEWER);
    setIsBulkModalOpen(false);
    setBulkError('');
  };

  const handleCloseBulkModal = () => {
    setIsBulkModalOpen(false);
    setBulkEmails('');
    setBulkRole(ROLES.VIEWER);
    setBulkError('');
  };

  const handleInvitationChange = (index, event) => {
    const { name, value } = event.target;
    const newInvitations = [...invitations];
    newInvitations[index][name] = value;
    setInvitations(newInvitations);
  };

  const sendInvitations = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setInvitationErrors([]);
    try {
      const filledInvitations = invitations.filter(invitation =>
        invitation?.email?.trim()
      );

      if (filledInvitations.length > availableSeats) {
        setLoading(false);
        openSeatLimitModal(
          getSeatLimitMessage(
            'Please upgrade your plan before sending more invitations.'
          )
        );
        return;
      }

      const result = await sendRequest(API_SET_INVITE_USER_URL, 'POST', {
        invitations: filledInvitations
      });
      console.log('Invitations sent:', result);

      // Handle different response formats
      let responseData;
      if (result?.data) {
        responseData = result.data;
      } else if (result && typeof result === 'object') {
        responseData = result;
      } else {
        throw new Error('Invalid response format from server');
      }

      const { created = 0, skipped = 0, errors = [] } = responseData;

      // Set success message if any invitations were created
      if (created > 0) {
        setSuccessMessage(
          `Successfully sent ${created} invitation${created > 1 ? 's' : ''}.`
        );
      }

      // Set errors if any
      if (errors && Array.isArray(errors) && errors.length > 0) {
        setInvitationErrors(errors);
        if (created === 0 && skipped === 0) {
          setError(
            'Failed to send invitations. Please check the errors below.'
          );
        } else if (created === 0) {
          setError(
            'No invitations were sent. Some invitations were skipped due to errors.'
          );
        }
      } else if (created === 0 && skipped > 0) {
        setError(`All ${skipped} invitation(s) were skipped.`);
      }

      // Clear form only if at least one invitation was created
      if (created > 0) {
        setInvitations([{ email: '', role: ROLES.VIEWER }]);
      }

      // Refresh the list of sent invitations
      fetchSentInvitations();
    } catch (e) {
      console.error('Error sending invitations:', e);

      // Try to extract error data from the error message or error object
      let errorData = null;
      let errorMessage = 'Failed to send invitations';

      // Check if error has response data (from sendRequest)
      if (e.response?.data) {
        errorData = e.response.data;
      } else if (e.data) {
        errorData = e.data;
      } else {
        // Try to parse error message if it contains JSON-like structure
        // or check if sendRequest stored the error data
        try {
          // Sometimes sendRequest might store error data in a custom property
          if (e.errorData) {
            errorData = e.errorData;
          }
        } catch (parseErr) {
          // Ignore parse errors
        }
      }

      // If we have errorData, extract message and errors
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        }
        if (errorData.data?.errors && Array.isArray(errorData.data.errors)) {
          setInvitationErrors(errorData.data.errors);
        }
        if (errorData.data) {
          const { created = 0, skipped = 0 } = errorData.data;
          if (created === 0 && skipped > 0) {
            errorMessage =
              errorData.message || `All ${skipped} invitation(s) were skipped.`;
          }
        }
      } else if (e.message) {
        errorMessage = e.message;
      } else if (typeof e === 'string') {
        errorMessage = e;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async invitation => {
    const confirmed = window.confirm(
      `Cancel the invitation sent to ${invitation.email}?`
    );

    if (!confirmed) return;

    setCancellingInvitationId(invitation.id);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await sendRequest(
        `${API_CANCEL_INVITE_URL}/${invitation.id}`,
        'PATCH'
      );

      if (result?.success === false) {
        throw new Error(result?.message || 'Failed to cancel invitation');
      }

      setSuccessMessage(`Invitation for ${invitation.email} was cancelled.`);
      fetchSentInvitations();
    } catch (e) {
      setError(e.message || 'Failed to cancel invitation');
    } finally {
      setCancellingInvitationId(null);
    }
  };

  const fetchSentInvitations = useCallback(async () => {
    setFetchingSentInvitations(true);
    setError(null);
    try {
      const result = await sendRequest(API_GET_INVITE_USER_URL, 'GET');
      // Handle response format: { success: true, data: [...] }
      const invitations = result?.data || result || [];
      setSentInvitations(Array.isArray(invitations) ? invitations : []);
    } catch (e) {
      setError(e.message || 'Failed to fetch invitations');
    } finally {
      setFetchingSentInvitations(false);
    }
  }, []);

  const fetchExistingUserCount = useCallback(async () => {
    try {
      const result = await sendRequest(API_GET_USERS_URL, 'GET');
      const users = result?.data || result || [];
      setExistingUserCount(Array.isArray(users) ? users.length : 0);
    } catch (e) {
      console.warn('Failed to fetch user count for pricing notice:', e);
      setExistingUserCount(0);
    }
  }, []);

  const fetchPlanSeatInfo = useCallback(async () => {
    try {
      const result = await sendRequest(API_GET_PLAN_SETTINGS_URL, 'GET');
      const rawPlanId =
        result?.usage?.plan_id ||
        result?.settings?.plan_id ||
        result?.settings?.planId ||
        result?.plan_id;

      if (!rawPlanId) {
        setPlanSeatInfo(SETUP_SEAT_ALLOWANCE);
        return;
      }

      const seatLimit = result?.usage?.seat_limit;
      if (seatLimit == null) {
        setPlanSeatInfo(SETUP_SEAT_ALLOWANCE);
        return;
      }
      setPlanSeatInfo({
        id: String(rawPlanId),
        name: result?.usage?.plan_name || 'Current plan',
        seats: seatLimit
      });
    } catch (e) {
      console.warn('Failed to fetch plan settings for seat limit:', e);
      setPlanSeatInfo(SETUP_SEAT_ALLOWANCE);
    }
  }, []);

  useEffect(() => {
    fetchSentInvitations();
    fetchExistingUserCount();
    fetchPlanSeatInfo();
  }, [fetchExistingUserCount, fetchPlanSeatInfo, fetchSentInvitations]);

  const getRoleDisplayName = role => {
    return ROLE_NAMES[role] || role;
  };
  const availableInviteRoles = Object.values(ROLES).filter(
    role => ![ROLES.DEMO_USER].includes(role)
  );
  const inviteRoleOptions = availableInviteRoles.map(role => ({
    value: role,
    label: getRoleDisplayName(role)
  }));

  const getStatusBadge = status => {
    const statusColors = {
      Pending:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      Accepted:
        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      Rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      Expired: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
      Cancelled:
        'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
    };
    const color = statusColors[status] || statusColors.Pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${color}`}>
        {status || 'Pending'}
      </span>
    );
  };

  return (
    <>
      <PageTitle>Payment & Billing</PageTitle>

      <div className="container mx-auto p-6 max-w-6xl">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 p-5 shadow-sm dark:border-slate-700 dark:from-slate-800 dark:to-slate-900">
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-start">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                {planSeatInfo.isSetupAllowance
                  ? 'Setup Seat Allowance'
                  : 'Current Plan'}
              </p>
              <h3 className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">
                {planSeatInfo.name}
              </h3>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                {planSeatInfo.isSetupAllowance
                  ? 'Before selecting a plan, your workspace can use up to 10 total seats during setup. When you choose a plan, it must include at least your active users plus pending invitations.'
                  : 'The logged-in user already occupies 1 seat, and pending invitations also count toward your seat limit until they are accepted, rejected, or expire.'}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[720px]">
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800 xl:min-h-[132px]">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Total Seats
                </p>
                <p className="mt-1 text-lg font-bold text-slate-900 dark:text-slate-100">
                  {maxSeats}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800 xl:min-h-[132px]">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Used Seats
                </p>
                <p className="mt-1 text-lg font-bold text-slate-900 dark:text-slate-100">
                  {usedSeats}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {existingUserCount} active + {pendingInvitationCount} pending
                </p>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-950/30 xl:min-h-[132px]">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
                  Available Seats
                </p>
                <p className="mt-1 text-lg font-bold text-blue-700 dark:text-blue-200">
                  {availableSeats}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Invite New Users Section */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 mb-8 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
              <PersonAddIcon className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Invite New Users
            </h2>
          </div>

          {successMessage && (
            <div
              className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 rounded-lg"
              role="alert"
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div
              className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg"
              role="alert"
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 dark:text-red-400 font-medium">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {invitationErrors.length > 0 && (
            <div
              className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-lg"
              role="alert"
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                    Invitation Errors:
                  </h3>
                  <ul className="list-disc list-inside space-y-1">
                    {invitationErrors.map((err, index) => (
                      <li
                        key={index}
                        className="text-sm text-yellow-700 dark:text-yellow-400"
                      >
                        <span className="font-medium">{err.email}:</span>{' '}
                        {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={e => {
              e.preventDefault();
              sendInvitations();
            }}
            className="space-y-4"
          >
            {invitations.map((invitation, index) => (
              <div
                key={index}
                className="grid gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 transition-all hover:shadow-md dark:border-slate-700 dark:bg-slate-900/50 lg:grid-cols-[minmax(0,1fr)_360px_auto] lg:items-end"
              >
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                    Email Address
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EmailIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      value={invitation.email}
                      onChange={e => handleInvitationChange(index, e)}
                      className="h-14 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 text-slate-900 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                    Access Level
                  </div>
                  <div className="flex items-center gap-2">
                    <BrandedSelect
                      value={invitation.role}
                      onChange={value =>
                        handleInvitationChange(index, {
                          target: { name: 'role', value }
                        })
                      }
                      options={inviteRoleOptions}
                      leadingIcon={ShieldOutlinedIcon}
                      className="min-w-0 flex-1"
                      minHeight={56}
                    />
                    <button
                      type="button"
                      onClick={() => setIsRoleInfoModalOpen(true)}
                      className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-500 transition-all hover:border-blue-400 hover:text-blue-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-blue-400"
                      aria-label="Open role descriptions"
                    >
                      <InfoOutlinedIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {invitations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveInvitation(index)}
                    className="flex h-14 items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3 font-semibold text-white shadow-md transition-all duration-200 hover:bg-red-600 hover:shadow-lg lg:min-w-[120px]"
                  >
                    <DeleteIcon className="h-5 w-5" />
                    Remove
                  </button>
                )}
              </div>
            ))}

            <div className="flex flex-wrap gap-3 pt-4">
              <button
                type="button"
                onClick={handleAddInvitation}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <AddIcon className="h-5 w-5" />
                Add Another
              </button>
              <button
                type="button"
                onClick={() => setIsBulkModalOpen(true)}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <UploadIcon className="h-5 w-5" />
                Bulk Add
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:shadow-none disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Skeleton className="h-3 w-20 rounded-sm bg-white/50" />
                    Sending...
                  </>
                ) : (
                  <>
                    <SendIcon className="h-5 w-5" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Sent Invitations Section */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
              <EmailIcon className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Sent Invitations
            </h2>
          </div>

          {fetchingSentInvitations && <Loading type="table" />}

          {!fetchingSentInvitations && sentInvitations.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700 mb-4">
                <EmailIcon className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-lg">
                No invitations sent yet.
              </p>
              <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">
                Start inviting users to get started!
              </p>
            </div>
          )}

          {!fetchingSentInvitations && sentInvitations.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Email
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Role
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Status
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Date
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sentInvitations.map((invite, index) => (
                    <tr
                      key={index}
                      className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <EmailIcon className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-900 dark:text-slate-100 font-medium">
                            {invite.email}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-700 dark:text-slate-300">
                          {getRoleDisplayName(invite.role)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(invite.status)}
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-500 dark:text-slate-400 text-sm">
                          {invite.createdAt
                            ? new Date(invite.createdAt).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {invite.status === 'Pending' ? (
                          <button
                            type="button"
                            onClick={() => handleCancelInvitation(invite)}
                            disabled={cancellingInvitationId === invite.id}
                            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-900/40"
                          >
                            <CloseIcon className="h-4 w-4" />
                            {cancellingInvitationId === invite.id
                              ? 'Cancelling...'
                              : 'Cancel'}
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            -
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isSeatLimitModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setIsSeatLimitModalOpen(false)}
          />
          <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Seat Limit Reached
                </p>
                <h3 className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">
                  {planSeatInfo.isSetupAllowance
                    ? 'Choose a plan to invite more users'
                    : 'Upgrade required to invite more users'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsSeatLimitModalOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                aria-label="Close seat limit modal"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
              {seatLimitMessage}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setIsSeatLimitModalOpen(false)}
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Close
              </button>
              <a
                href="/settings/plan-configuration"
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                {planSeatInfo.isSetupAllowance ? 'Choose Plan' : 'Upgrade Plan'}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Role Information Modal */}
      {isRoleInfoModalOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-8">
            <button
              type="button"
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={() => setIsRoleInfoModalOpen(false)}
              aria-label="Close role information modal"
            />

            <div className="relative z-[10000] w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-8 shadow-2xl dark:border-slate-700 dark:bg-slate-800">
              <div className="mb-6 flex items-center justify-between gap-3">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Role Permissions Guide
                </h3>
                <button
                  type="button"
                  onClick={() => setIsRoleInfoModalOpen(false)}
                  className="rounded-lg p-1 text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-300"
                  aria-label="Close role information modal"
                >
                  <CloseIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-5 text-sm leading-6 text-slate-600 dark:text-slate-300">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/50">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    Viewer
                  </p>
                  <p>
                    Can view all analytics and data across the panel, but has no
                    administrative privileges beyond managing their own account
                    settings.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/50">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    Panel Admin
                  </p>
                  <p>
                    Can manage developers, repositories, tags, and projects
                    related to GitMe usage. Does not have access to Git
                    integrations, plan selection, budget settings, payment
                    methods, or user invitations.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/50">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    Account Admin
                  </p>
                  <p>
                    Has full administrative access across the workspace,
                    including all configuration, billing, integration, and user
                    management capabilities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {isBulkModalOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
              onClick={handleCloseBulkModal}
            ></div>

            {/* Modal panel */}
            <div className="relative z-[10000] inline-block align-bottom bg-white dark:bg-slate-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white dark:bg-slate-800 px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                      <UploadIcon className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                      Bulk Add Emails
                    </h3>
                  </div>
                  <button
                    onClick={handleCloseBulkModal}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    <CloseIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Email Addresses
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                      Enter email addresses separated by commas, semicolons, or
                      new lines
                    </p>
                    <textarea
                      value={bulkEmails}
                      onChange={e => {
                        setBulkEmails(e.target.value);
                        setBulkError('');
                      }}
                      placeholder="user1@example.com, user2@example.com&#10;user3@example.com&#10;user4@example.com"
                      rows={8}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Role for All Emails
                    </label>
                    <BrandedSelect
                      value={bulkRole}
                      onChange={setBulkRole}
                      options={inviteRoleOptions}
                      helperText="Apply the same access level to every email address in this batch."
                      leadingIcon={ShieldOutlinedIcon}
                    />
                  </div>

                  {bulkError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-400">
                        {bulkError}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-900/50 px-6 py-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseBulkModal}
                  className="px-6 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkAdd}
                  className="px-6 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <AddIcon className="h-5 w-5" />
                  Add to List
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InviteUser;
